import { useTheme } from "@/lib/ground";
import { kitEdge, onColor } from "@/lib/team-color";
import { cn } from "@/lib/utils";

/**
 * A team's mark.
 *
 * Crests and wordmarks are rarely circular, so a real logo is never masked:
 * `object-contain`, no radius, no background plate, no shadow: a rectangular
 * shadow behind a transparent PNG is exactly the artefact this avoids. The
 * circle is only ever the FALLBACK disc carrying the team's code.
 *
 * Codes stay in NXT, independently of the page's display face. No synthetic
 * bold or added tracking; `text-[0.5em]` scales the label with the disc.
 */
export function TeamBadge({
  logo,
  color,
  short,
  size = "md",
  className,
  loading,
}: {
  logo?: string | undefined;
  color: string;
  short: string;
  size?: "sm" | "md" | "lg";
  className?: string | undefined;
  loading?: "lazy" | "eager" | undefined;
}) {
  const box = size === "sm" ? "size-6" : size === "lg" ? "size-12" : "size-8";
  const theme = useTheme();

  if (logo !== undefined) {
    return (
      <img
        className={cn(box, "shrink-0 object-contain", className)}
        src={logo}
        alt=""
        loading={loading}
        decoding="async"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      data-edge={kitEdge(color, theme)}
      className={cn(
        box,
        "kit-edge inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full",
        "team-code text-[0.5em] leading-none",
        className,
      )}
      style={{ background: color, color: onColor(color) }}
    >
      {short.slice(0, 4)}
    </span>
  );
}
